import {
  AccessTimeOutlined,
  CalendarMonthOutlined,
  Check,
  ChevronLeft,
  ChevronRight,
  Close,
  ExpandLess,
  ExpandMore,
  Inventory2Outlined,
  LocalPrintshopOutlined,
  OpenInFullOutlined,
  ReceiptLongOutlined,
  StorefrontOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { storeInvoice } from "../../components/Pdf/storeInvoice";
import {
  StoreTransferHistoryList,
  updateStoreRequest,
} from "../../services/inventoryService";
import { RootState } from "../../store";

type TransferStatus = "PENDING" | "APPROVED" | "REJECTED";
type QuantityField = "requested_qty_packed" | "requested_qty_unpacked";

interface TransferItem {
  transfer_id: string;
  invoice_no: string;
  product_id: string;
  qty_packed: string | number;
  qty_unpacked: string | number | null;
  requested_qty_packed: string | number;
  requested_qty_unpacked: string | number | null;
  status: TransferStatus;
  notes: string;
  created_at: string;
  sending_store: string;
  receiving_store: string;
  store: string;
  store_type: string;
  product: string;
  category: string | null;
  sub_category: string | null;
}

interface TransferFilter {
  status: TransferStatus;
  page: number;
  limit: number;
  count: number;
  total: number;
}

interface SubCategoryGroup {
  name: string;
  items: TransferItem[];
  productCount: number;
  totalQty: number;
  unpackedQty: number;
}

interface CategoryGroup {
  name: string;
  subCategories: SubCategoryGroup[];
  productCount: number;
  totalQty: number;
  unpackedQty: number;
}

interface InvoiceGroup {
  invoiceNo: string;
  status: TransferStatus;
  createdAt: string;
  store: string;
  notes: string;
  categories: CategoryGroup[];
  items: TransferItem[];
  categoryCount: number;
  productCount: number;
}

interface ApiResponse {
  data?: TransferItem[];
  page?: number;
  limit?: number;
  count?: number;
  total?: number;
}

const GREEN = "#075b28";
const DARK_GREEN = "#03451e";
const LIGHT_GREEN = "#f5faf7";
const BORDER = "#dfe5e1";
const MUTED = "#65706a";
const DEFAULT_CATEGORY = "Uncategorized";
const DEFAULT_SUB_CATEGORY = "Other";

const numberValue = (value: string | number | null | undefined): number => {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const displayName = (
  value: string | null | undefined,
  fallback: string,
): string => {
  const normalized = value?.trim();
  if (!normalized) return fallback;

  return normalized
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const groupTransfers = (data: TransferItem[]): InvoiceGroup[] => {
  type MutableCategory = CategoryGroup & {
    subCategoryMap: Map<string, SubCategoryGroup>;
  };

  type MutableInvoice = {
    invoiceNo: string;
    status: TransferStatus;
    createdAt: string;
    store: string;
    notes: string;
    items: TransferItem[];
    productCount: number;
    categoryMap: Map<string, MutableCategory>;
  };

  const invoiceMap = new Map<string, MutableInvoice>();

  data.forEach((item) => {
    const categoryName = displayName(item.category, DEFAULT_CATEGORY);
    const subCategoryName = displayName(
      item.sub_category,
      DEFAULT_SUB_CATEGORY,
    );

    let invoice = invoiceMap.get(item.invoice_no);
    if (!invoice) {
      invoice = {
        invoiceNo: item.invoice_no,
        status: item.status,
        createdAt: item.created_at,
        store: item.store,
        notes: item.notes ?? "",
        items: [],
        productCount: 0,
        categoryMap: new Map(),
      };
      invoiceMap.set(item.invoice_no, invoice);
    }

    invoice.items.push(item);
    invoice.productCount += 1;

    let category = invoice.categoryMap.get(categoryName);
    if (!category) {
      category = {
        name: categoryName,
        subCategories: [],
        productCount: 0,
        totalQty: 0,
        unpackedQty: 0,
        subCategoryMap: new Map(),
      };
      invoice.categoryMap.set(categoryName, category);
    }

    category.productCount += 1;
    category.totalQty += numberValue(item.requested_qty_packed);
    category.unpackedQty += numberValue(item.qty_unpacked);

    let subCategory = category.subCategoryMap.get(subCategoryName);
    if (!subCategory) {
      subCategory = {
        name: subCategoryName,
        items: [],
        productCount: 0,
        totalQty: 0,
        unpackedQty: 0,
      };
      category.subCategoryMap.set(subCategoryName, subCategory);
    }

    subCategory.items.push(item);
    subCategory.productCount += 1;
    subCategory.totalQty += numberValue(item.requested_qty_packed);
    subCategory.unpackedQty += numberValue(item.qty_unpacked);
  });

  return Array.from(invoiceMap.values()).map((invoice) => {
    const categories = Array.from(invoice.categoryMap.values()).map(
      (category) => ({
        name: category.name,
        productCount: category.productCount,
        totalQty: category.totalQty,
        unpackedQty: category.unpackedQty,
        subCategories: Array.from(category.subCategoryMap.values()),
      }),
    );

    return {
      invoiceNo: invoice.invoiceNo,
      status: invoice.status,
      createdAt: invoice.createdAt,
      store: invoice.store,
      notes: invoice.notes,
      categories,
      items: invoice.items,
      categoryCount: categories.length,
      productCount: invoice.productCount,
    };
  });
};

const StatusBadge: React.FC<{ status: TransferStatus }> = ({ status }) => {
  const config = {
    PENDING: {
      label: "Pending",
      color: "#8a5a00",
      backgroundColor: "#fff8e6",
      borderColor: "#efc96c",
    },
    APPROVED: {
      label: "Approved",
      color: GREEN,
      backgroundColor: "#eef8f1",
      borderColor: "#a9d2b8",
    },
    REJECTED: {
      label: "Rejected",
      color: "#c62828",
      backgroundColor: "#fff1f1",
      borderColor: "#efb3b3",
    },
  }[status];

  return (
    <Chip
      size="small"
      label={config.label}
      icon={
        <Box
          component="span"
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            backgroundColor: config.color,
          }}
        />
      }
      sx={{
        height: 28,
        color: config.color,
        backgroundColor: config.backgroundColor,
        border: `1px solid ${config.borderColor}`,
        fontWeight: 600,
        "& .MuiChip-label": { px: 1 },
        "& .MuiChip-icon": { ml: 1 },
      }}
    />
  );
};

const RequestedProducts: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [filter, setFilter] = useState<TransferFilter>({
    status: "PENDING",
    page: 1,
    limit: 10,
    count: 0,
    total: 1,
  });
  const [items, setItems] = useState<TransferItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [updatingInvoice, setUpdatingInvoice] = useState<string | null>(null);
  const [expandedInvoices, setExpandedInvoices] = useState<Set<string>>(
    new Set(),
  );
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(),
  );
  const [expandedSubCategories, setExpandedSubCategories] = useState<
    Set<string>
  >(new Set());

  const invoices = useMemo(() => groupTransfers(items), [items]);
  const pageCount = Math.max(filter.total, 1);
  const firstResult =
    filter.count === 0 ? 0 : (filter.page - 1) * filter.limit + 1;
  const lastResult = Math.min(filter.page * filter.limit, filter.count);

  useEffect(() => {
    const loadTransfers = async () => {
      setLoading(true);
      try {
        const response: ApiResponse = await StoreTransferHistoryList({
          status: filter.status,
          page: filter.page,
          limit: filter.limit,
        });

        setItems(response?.data ?? []);
        setFilter((current) => ({
          ...current,
          page: response?.page ?? current.page,
          limit: response?.limit ?? current.limit,
          count: response?.count ?? 0,
          total: Math.max(response?.total ?? 1, 1),
        }));
      } catch (error) {
        console.error("Unable to fetch transfer requests:", error);
        setItems([]);
      } finally {
        setLoading(false);
      }
    };

    void loadTransfers();
  }, [filter.status, filter.page, filter.limit]);

  const toggleSetValue = (
    setter: React.Dispatch<React.SetStateAction<Set<string>>>,
    value: string,
  ) => {
    setter((current) => {
      const next = new Set(current);
      next.has(value) ? next.delete(value) : next.add(value);
      return next;
    });
  };

  const expandAllForInvoice = (invoice: InvoiceGroup) => {
    const categoryKeys = invoice.categories.map(
      (category) => `${invoice.invoiceNo}:${category.name}`,
    );
    const subCategoryKeys = invoice.categories.flatMap((category) =>
      category.subCategories.map(
        (subCategory) =>
          `${invoice.invoiceNo}:${category.name}:${subCategory.name}`,
      ),
    );

    const allExpanded =
      categoryKeys.every((key) => expandedCategories.has(key)) &&
      subCategoryKeys.every((key) => expandedSubCategories.has(key));

    setExpandedInvoices((current) => new Set(current).add(invoice.invoiceNo));
    setExpandedCategories((current) => {
      const next = new Set(current);
      categoryKeys.forEach((key) =>
        allExpanded ? next.delete(key) : next.add(key),
      );
      return next;
    });
    setExpandedSubCategories((current) => {
      const next = new Set(current);
      subCategoryKeys.forEach((key) =>
        allExpanded ? next.delete(key) : next.add(key),
      );
      return next;
    });
  };

  const updateLocalQuantity = (
    transferId: string,
    field: QuantityField,
    rawValue: string,
  ) => {
    let value: number | "";

    if (rawValue === "") {
      value = "";
    } else {
      const parsedValue = Math.max(0, Number(rawValue));

      value =
        field === "requested_qty_packed"
          ? Math.floor(parsedValue)
          : Math.round((parsedValue + Number.EPSILON) * 100) / 100;
    }

    setItems((current) =>
      current.map((item) =>
        item.transfer_id === transferId ? { ...item, [field]: value } : item,
      ),
    );
  };

  const handleItemUpdate = async (item: TransferItem) => {
    setUpdatingId(item.transfer_id);
    try {
      await updateStoreRequest(item);
      alert("Transfer item updated successfully.");
    } catch (error) {
      console.error("Unable to update transfer item:", error);
      alert("Something went wrong while updating the transfer item.");
    } finally {
      setUpdatingId(null);
    }
  };

  const handleInvoiceStatus = async (
    invoice: InvoiceGroup,
    status: "APPROVED" | "REJECTED",
  ) => {
    setUpdatingInvoice(invoice.invoiceNo);
    try {
      await Promise.all(
        invoice.items.map((item) => updateStoreRequest({ ...item, status })),
      );
      setItems((current) =>
        current.filter((item) => item.invoice_no !== invoice.invoiceNo),
      );
    } catch (error) {
      console.error(`Unable to mark invoice as ${status}:`, error);
      alert(
        `Something went wrong while updating invoice ${invoice.invoiceNo}.`,
      );
    } finally {
      setUpdatingInvoice(null);
    }
  };

  const handlePrint = async (invoice: InvoiceGroup) => {
    try {
      const requestedBy = `${user?.first_name || ""} ${user?.last_name || ""}`;
      await storeInvoice(invoice.items, requestedBy, invoice.notes);
    } catch (error) {
      console.error("Unable to print transfer request:", error);
      alert("Something went wrong while printing the transfer request.");
    }
  };

  const handleStatusChange = (event: SelectChangeEvent<TransferStatus>) => {
    setExpandedInvoices(new Set());
    setExpandedCategories(new Set());
    setExpandedSubCategories(new Set());
    setFilter((current) => ({
      ...current,
      status: event.target.value as TransferStatus,
      page: 1,
    }));
  };

  const handleRowsPerPageChange = (event: SelectChangeEvent<number>) => {
    setFilter((current) => ({
      ...current,
      limit: Number(event.target.value),
      page: 1,
    }));
  };

  const formatDate = (value: string) =>
    new Intl.DateTimeFormat("en-CA", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date(value));

  const formatTime = (value: string) =>
    new Intl.DateTimeFormat("en-CA", {
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
    }).format(new Date(value));

  const renderProducts = (subCategory: SubCategoryGroup) => (
    <TableContainer sx={{ overflowX: "auto" }}>
      <Table
        size="small"
        sx={{
          minWidth: 560,
          "& .MuiTableCell-root": {
            py: 0.7,
            px: 1.25,
            fontSize: 13,
            borderColor: BORDER,
          },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 60, fontWeight: 700 }}>No.</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Product</TableCell>
            <TableCell align="center" sx={{ width: 160, fontWeight: 700 }}>
              Requested Qty
            </TableCell>
            <TableCell align="center" sx={{ width: 135, fontWeight: 700 }}>
              Unpacked
            </TableCell>
            <TableCell align="center" sx={{ width: 120, fontWeight: 700 }}>
              Update
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {subCategory.items.map((product, index) => {
            const disableEdit = Boolean(
              product?.created_at &&
              Date.now() - new Date(product.created_at).getTime() >
                24 * 60 * 60 * 1000,
            );
            return (
              <TableRow key={product.transfer_id} hover>
                <TableCell>{index + 1}</TableCell>
                <TableCell sx={{ fontWeight: 500 }}>
                  {product.product}
                </TableCell>
                <TableCell align="center">
                  {filter.status === "PENDING" ? (
                    <TextField
                      size="small"
                      type="number"
                      disabled={disableEdit}
                      value={product.requested_qty_packed ?? ""}
                      onChange={(event) =>
                        updateLocalQuantity(
                          product.transfer_id,
                          "requested_qty_packed",
                          event.target.value,
                        )
                      }
                      inputProps={{ min: 0 }}
                      sx={{
                        width: 64,
                        "& .MuiInputBase-root": { height: 32 },
                        "& input": { py: 0.4, px: 1 },
                      }}
                    />
                  ) : (
                    product.requested_qty_packed
                  )}
                </TableCell>
                <TableCell align="center">
                  {filter.status === "PENDING" ? (
                    <TextField
                      size="small"
                      type="number"
                      value={product.requested_qty_unpacked ?? ""}
                      disabled={disableEdit}
                      onChange={(event) =>
                        updateLocalQuantity(
                          product.transfer_id,
                          "requested_qty_unpacked",
                          event.target.value,
                        )
                      }
                      slotProps={{
                        htmlInput: {
                          min: 0,
                          step: 0.01,
                          inputMode: "decimal",
                        },
                      }}
                      sx={{
                        width: 72,
                        "& .MuiInputBase-root": { height: 32 },
                        "& input": { py: 0.4, px: 1 },
                      }}
                    />
                  ) : (
                    (product.requested_qty_unpacked ?? "-")
                  )}
                </TableCell>
                <TableCell align="center">
                  {filter.status === "PENDING" && (
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={
                        updatingId === product.transfer_id || disableEdit
                      }
                      onClick={() => void handleItemUpdate(product)}
                      sx={{
                        minWidth: 68,
                        height: 29,
                        px: 1,
                        color: GREEN,
                        borderColor: "#9dc8ad",
                        fontSize: 10.5,
                        fontWeight: 700,
                        textTransform: "uppercase",
                      }}
                    >
                      {updatingId === product.transfer_id ? "Saving" : "Update"}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderInvoiceDetails = (invoice: InvoiceGroup) => (
    <Box sx={{ px: { xs: 0.75, sm: 1.25 }, pb: 1.25 }}>
      <Box
        sx={{
          minHeight: 50,
          px: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          border: `1px solid ${BORDER}`,
          borderRadius: 2,
          backgroundColor: "#fff",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.25}
          flexWrap="wrap"
        >
          <Typography sx={{ color: GREEN, fontWeight: 700 }}>
            Categories
          </Typography>
          <Typography variant="body2" color={MUTED}>
            {invoice.categoryCount} Categories&nbsp;&nbsp;•&nbsp;&nbsp;
            {invoice.productCount} Products
          </Typography>
        </Stack>

        <Button
          size="small"
          variant="outlined"
          startIcon={<OpenInFullOutlined sx={{ fontSize: 17 }} />}
          onClick={() => expandAllForInvoice(invoice)}
          sx={{
            minWidth: "fit-content",
            height: 34,
            color: "#303733",
            borderColor: BORDER,
            textTransform: "none",
          }}
        >
          {isMobile ? "Expand" : "Expand All"}
        </Button>
      </Box>

      <Stack spacing={0.75} sx={{ mt: 0.75 }}>
        {invoice.categories.map((category) => {
          const categoryKey = `${invoice.invoiceNo}:${category.name}`;
          const categoryExpanded = expandedCategories.has(categoryKey);

          return (
            <Box
              key={categoryKey}
              sx={{
                border: `1px solid ${BORDER}`,
                borderRadius: 2,
                overflow: "hidden",
                backgroundColor: "#fff",
              }}
            >
              <Box
                role="button"
                tabIndex={0}
                onClick={() =>
                  toggleSetValue(setExpandedCategories, categoryKey)
                }
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    toggleSetValue(setExpandedCategories, categoryKey);
                  }
                }}
                sx={{
                  minHeight: 48,
                  px: { xs: 1, sm: 1.5 },
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  cursor: "pointer",
                  backgroundColor: categoryExpanded ? LIGHT_GREEN : "#fff",
                }}
              >
                {categoryExpanded ? (
                  <ExpandMore sx={{ fontSize: 20 }} />
                ) : (
                  <ChevronRight sx={{ fontSize: 20 }} />
                )}
                <Inventory2Outlined sx={{ color: GREEN, fontSize: 21 }} />
                <Typography sx={{ color: DARK_GREEN, fontWeight: 700 }}>
                  {category.name}
                </Typography>
                <Chip
                  size="small"
                  label={`${category.productCount} Products`}
                  sx={{
                    height: 25,
                    color: DARK_GREEN,
                    backgroundColor: "#f7fbf8",
                    border: `1px solid ${BORDER}`,
                    borderRadius: 1,
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                />

                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={{ xs: 1, sm: 3 }}
                  sx={{ ml: "auto", whiteSpace: "nowrap" }}
                >
                  <Typography variant="body2">
                    Total Qty: {category.totalQty}
                  </Typography>
                  <Typography variant="body2" sx={{ color: GREEN }}>
                    Unpacked: {category.unpackedQty}
                  </Typography>
                  {categoryExpanded ? (
                    <ExpandLess sx={{ color: GREEN }} />
                  ) : (
                    <ExpandMore sx={{ color: GREEN }} />
                  )}
                </Stack>
              </Box>

              <Collapse in={categoryExpanded} timeout="auto" unmountOnExit>
                <Stack spacing={0.75} sx={{ p: { xs: 0.75, sm: 1 } }}>
                  {category.subCategories.map((subCategory) => {
                    const subCategoryKey = `${invoice.invoiceNo}:${category.name}:${subCategory.name}`;
                    const subCategoryExpanded =
                      expandedSubCategories.has(subCategoryKey);

                    return (
                      <Box
                        key={subCategoryKey}
                        sx={{
                          border: `1px solid ${BORDER}`,
                          borderRadius: 2,
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          role="button"
                          tabIndex={0}
                          onClick={() =>
                            toggleSetValue(
                              setExpandedSubCategories,
                              subCategoryKey,
                            )
                          }
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              toggleSetValue(
                                setExpandedSubCategories,
                                subCategoryKey,
                              );
                            }
                          }}
                          sx={{
                            minHeight: 46,
                            px: { xs: 1, sm: 1.5 },
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            cursor: "pointer",
                            backgroundColor: "#f9fbfa",
                          }}
                        >
                          {subCategoryExpanded ? (
                            <ExpandMore sx={{ fontSize: 20 }} />
                          ) : (
                            <ChevronRight sx={{ fontSize: 20 }} />
                          )}
                          <Typography
                            sx={{ color: DARK_GREEN, fontWeight: 700 }}
                          >
                            {subCategory.name}
                          </Typography>
                          <Chip
                            size="small"
                            label={`${subCategory.productCount} Products`}
                            sx={{
                              height: 24,
                              color: DARK_GREEN,
                              backgroundColor: "#fff",
                              border: `1px solid ${BORDER}`,
                              borderRadius: 1,
                              fontSize: 11,
                              fontWeight: 600,
                            }}
                          />

                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={{ xs: 1, sm: 3 }}
                            sx={{ ml: "auto", whiteSpace: "nowrap" }}
                          >
                            <Typography variant="body2">
                              Total Qty: {subCategory.totalQty}
                            </Typography>
                            <Typography variant="body2" sx={{ color: GREEN }}>
                              Unpacked: {subCategory.unpackedQty}
                            </Typography>
                            {subCategoryExpanded ? (
                              <ExpandLess sx={{ color: GREEN }} />
                            ) : (
                              <ChevronRight sx={{ color: GREEN }} />
                            )}
                          </Stack>
                        </Box>

                        <Collapse
                          in={subCategoryExpanded}
                          timeout="auto"
                          unmountOnExit
                        >
                          {renderProducts(subCategory)}
                        </Collapse>
                      </Box>
                    );
                  })}
                </Stack>
              </Collapse>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );

  return (
    <Box sx={{ p: { xs: 0.75, sm: 1.5 }, backgroundColor: "#f8faf9" }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1, sm: 1.75 },
          border: `1px solid ${BORDER}`,
          borderRadius: 3,
          boxShadow: "0 2px 10px rgba(20,55,35,0.05)",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          spacing={1.25}
          sx={{ mb: 1.5, px: 0.5 }}
        >
          <Typography sx={{ fontSize: 17, fontWeight: 700 }}>
            Orders by {user?.first_name} {user?.last_name}
          </Typography>

          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2" fontWeight={600}>
              Status
            </Typography>
            <FormControl size="small" sx={{ minWidth: 145, ml: "auto" }}>
              <Select<TransferStatus>
                value={filter.status}
                onChange={handleStatusChange}
                sx={{ height: 40, borderRadius: 2 }}
              >
                <MenuItem value="PENDING">Pending</MenuItem>
                <MenuItem value="APPROVED">Approved</MenuItem>
                <MenuItem value="REJECTED">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Stack>

        {loading ? (
          <Box sx={{ minHeight: 280, display: "grid", placeItems: "center" }}>
            <CircularProgress sx={{ color: GREEN }} />
          </Box>
        ) : invoices.length === 0 ? (
          <Box
            sx={{
              minHeight: 260,
              display: "grid",
              placeItems: "center",
              border: `1px solid ${BORDER}`,
              borderRadius: 2,
            }}
          >
            <Stack alignItems="center" spacing={1}>
              <Inventory2Outlined sx={{ fontSize: 44, color: "grey.400" }} />
              <Typography fontWeight={600}>
                No transfer requests found
              </Typography>
            </Stack>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: { xs: "none", sm: "block" },
                height: "68vh",
                overflow: "auto",
              }}
            >
              <TableContainer
                sx={{
                  border: `1px solid ${BORDER}`,
                  borderRadius: 2.5,
                  // overflow: "hidden",
                }}
              >
                <Table sx={{ minWidth: 980 }} stickyHeader>
                  <TableHead
                    sx={{
                      // display: "flex",
                      position: "sticky",
                      zIndex: 20,
                      backgroundColor: "white",
                      top: 0,
                    }}
                  >
                    <TableRow>
                      {/* <TableCell sx={{ width: 44 }} /> */}
                      {[
                        "No.",
                        "Store",
                        "Invoice No.",
                        "Requested Date",
                        "Print",
                        "Status",
                      ].map((heading) => (
                        <TableCell
                          key={heading}
                          align="left"
                          sx={{
                            // py: 1.1,
                            px: 1,
                            fontSize: 13,
                            fontWeight: 700,
                            whiteSpace: "nowrap",
                            borderColor: BORDER,
                          }}
                        >
                          {heading}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {invoices.map((invoice, index) => {
                      const expanded = expandedInvoices.has(invoice.invoiceNo);
                      const invoiceUpdating =
                        updatingInvoice === invoice.invoiceNo;

                      return (
                        <Fragment key={invoice.invoiceNo}>
                          <TableRow
                            hover
                            sx={{
                              "& > .MuiTableCell-root": {
                                py: 1,
                                px: 1,
                                borderColor: BORDER,
                              },
                            }}
                          >
                            {/* <TableCell align="center">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  toggleSetValue(
                                    setExpandedInvoices,
                                    invoice.invoiceNo,
                                  )
                                }
                                sx={{ color: GREEN }}
                              >
                                {expanded ? <ExpandMore /> : <ChevronRight />}
                              </IconButton>
                            </TableCell> */}
                            <TableCell align="center">
                              {(filter.page - 1) * filter.limit + index + 1}
                            </TableCell>
                            <TableCell>
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1.1}
                              >
                                <Box
                                  sx={{
                                    // width: 92,
                                    height: 32,
                                    borderRadius: "50%",
                                    display: "flex",
                                    flexDirection: "row",
                                    gap: 3,
                                    placeItems: "left",
                                    backgroundColor: "#edf7f0",
                                    color: GREEN,
                                  }}
                                >
                                  <StorefrontOutlined sx={{ fontSize: 20 }} />
                                  <Typography fontWeight={600}>
                                    {invoice.store}
                                  </Typography>
                                </Box>
                              </Stack>
                            </TableCell>
                            <TableCell align="left">
                              <Button
                                variant="contained"
                                startIcon={<ReceiptLongOutlined />}
                                onClick={() =>
                                  toggleSetValue(
                                    setExpandedInvoices,
                                    invoice.invoiceNo,
                                  )
                                }
                                sx={{
                                  // minWidth: 116,
                                  height: 40,
                                  borderRadius: 2,
                                  backgroundColor: GREEN,
                                  textTransform: "none",
                                  fontWeight: 700,
                                  boxShadow: "0 3px 8px rgba(7,91,40,.25)",
                                  "&:hover": { backgroundColor: DARK_GREEN },
                                }}
                              >
                                {invoice.invoiceNo}
                              </Button>
                            </TableCell>
                            <TableCell>
                              <Stack spacing={0.55} direction={"row"}>
                                <Stack
                                  direction="row"
                                  spacing={0.8}
                                  alignItems="center"
                                >
                                  <CalendarMonthOutlined
                                    sx={{ color: GREEN, fontSize: 20 }}
                                  />
                                  <Typography fontWeight={600} fontSize={13}>
                                    {formatDate(invoice.createdAt)}
                                  </Typography>
                                </Stack>
                                <Stack
                                  direction="row"
                                  spacing={0.8}
                                  alignItems="center"
                                >
                                  <AccessTimeOutlined
                                    sx={{ color: GREEN, fontSize: 20 }}
                                  />
                                  <Typography fontSize={13}>
                                    {formatTime(invoice.createdAt)}
                                  </Typography>
                                </Stack>
                              </Stack>
                            </TableCell>
                            <TableCell align="left">
                              <Tooltip title="Print">
                                <IconButton
                                  onClick={() => void handlePrint(invoice)}
                                  sx={{
                                    width: 38,
                                    height: 38,
                                    border: `1px solid ${BORDER}`,
                                    borderRadius: 1.5,
                                    color: "#4b514e",
                                  }}
                                >
                                  <LocalPrintshopOutlined />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="left">
                              <StatusBadge status={invoice.status} />
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell colSpan={8} sx={{ p: 0, border: 0 }}>
                              <Collapse
                                in={expanded}
                                timeout="auto"
                                unmountOnExit
                              >
                                {renderInvoiceDetails(invoice)}
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            <Stack spacing={1} sx={{ display: { xs: "flex", sm: "none" } }}>
              {invoices.map((invoice, index) => {
                const expanded = expandedInvoices.has(invoice.invoiceNo);
                const invoiceUpdating = updatingInvoice === invoice.invoiceNo;

                return (
                  <Paper
                    key={invoice.invoiceNo}
                    elevation={0}
                    sx={{ border: `1px solid ${BORDER}`, borderRadius: 2.5 }}
                  >
                    <Box sx={{ p: 1.25 }}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <IconButton
                            size="small"
                            onClick={() =>
                              toggleSetValue(
                                setExpandedInvoices,
                                invoice.invoiceNo,
                              )
                            }
                            sx={{ color: GREEN }}
                          >
                            {expanded ? <ExpandMore /> : <ChevronRight />}
                          </IconButton>
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: "50%",
                              display: "grid",
                              placeItems: "center",
                              backgroundColor: "#edf7f0",
                              color: GREEN,
                            }}
                          >
                            <StorefrontOutlined sx={{ fontSize: 20 }} />
                          </Box>
                          <Box>
                            <Typography fontWeight={700}>
                              {invoice.store}
                            </Typography>
                            <Typography variant="caption" color={MUTED}>
                              #{(filter.page - 1) * filter.limit + index + 1}
                            </Typography>
                          </Box>
                        </Stack>
                        <StatusBadge status={invoice.status} />
                      </Stack>

                      <Stack direction="row" spacing={1} sx={{ mt: 1.25 }}>
                        <Button
                          variant="contained"
                          startIcon={<ReceiptLongOutlined />}
                          sx={{
                            flex: 1,
                            height: 38,
                            backgroundColor: GREEN,
                            textTransform: "none",
                            fontWeight: 700,
                            "&:hover": { backgroundColor: DARK_GREEN },
                          }}
                        >
                          {invoice.invoiceNo}
                        </Button>
                        <IconButton
                          onClick={() => void handlePrint(invoice)}
                          sx={{
                            border: `1px solid ${BORDER}`,
                            borderRadius: 1.5,
                          }}
                        >
                          <LocalPrintshopOutlined />
                        </IconButton>
                      </Stack>

                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        sx={{ mt: 1.15 }}
                      >
                        <Stack
                          direction="row"
                          spacing={0.7}
                          alignItems="center"
                        >
                          <CalendarMonthOutlined
                            sx={{ color: GREEN, fontSize: 18 }}
                          />
                          <Typography variant="body2">
                            {formatDate(invoice.createdAt)}
                          </Typography>
                        </Stack>
                        <Stack
                          direction="row"
                          spacing={0.7}
                          alignItems="center"
                        >
                          <AccessTimeOutlined
                            sx={{ color: GREEN, fontSize: 18 }}
                          />
                          <Typography variant="body2">
                            {formatTime(invoice.createdAt)}
                          </Typography>
                        </Stack>
                      </Stack>

                      <Stack direction="row" spacing={0.75} sx={{ mt: 1.25 }}>
                        <Button
                          fullWidth
                          size="small"
                          variant="outlined"
                          startIcon={<VisibilityOutlined />}
                          onClick={() =>
                            toggleSetValue(
                              setExpandedInvoices,
                              invoice.invoiceNo,
                            )
                          }
                          sx={{
                            color: GREEN,
                            borderColor: BORDER,
                            textTransform: "none",
                          }}
                        >
                          View
                        </Button>
                        {invoice.status === "PENDING" && (
                          <>
                            <IconButton
                              disabled={invoiceUpdating}
                              onClick={() =>
                                void handleInvoiceStatus(invoice, "APPROVED")
                              }
                              sx={{
                                color: GREEN,
                                border: `1px solid ${BORDER}`,
                              }}
                            >
                              <Check />
                            </IconButton>
                            <IconButton
                              disabled={invoiceUpdating}
                              onClick={() =>
                                void handleInvoiceStatus(invoice, "REJECTED")
                              }
                              sx={{
                                color: "#d52626",
                                border: "1px solid #efb6b6",
                              }}
                            >
                              <Close />
                            </IconButton>
                          </>
                        )}
                      </Stack>
                    </Box>

                    <Collapse in={expanded} timeout="auto" unmountOnExit>
                      {renderInvoiceDetails(invoice)}
                    </Collapse>
                  </Paper>
                );
              })}
            </Stack>
          </>
        )}

        {!loading && invoices.length > 0 && (
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems="center"
            spacing={1.5}
            sx={{ mt: 1.75, px: 0.5 }}
          >
            <Typography variant="body2" color={MUTED}>
              Showing {firstResult} to {lastResult} of {filter.count} results
            </Typography>

            <Stack direction="row" spacing={0.75} alignItems="center">
              <IconButton
                size="small"
                disabled={filter.page <= 1}
                onClick={() =>
                  setFilter((current) => ({
                    ...current,
                    page: current.page - 1,
                  }))
                }
                sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5 }}
              >
                <ChevronLeft />
              </IconButton>
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  display: "grid",
                  placeItems: "center",
                  borderRadius: 1.5,
                  color: "white",
                  backgroundColor: GREEN,
                  fontWeight: 700,
                }}
              >
                {filter.page}
              </Box>
              <IconButton
                size="small"
                disabled={filter.page >= pageCount}
                onClick={() =>
                  setFilter((current) => ({
                    ...current,
                    page: current.page + 1,
                  }))
                }
                sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5 }}
              >
                <ChevronRight />
              </IconButton>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color={MUTED}>
                Rows per page:
              </Typography>
              <FormControl size="small">
                <Select<number>
                  value={filter.limit}
                  onChange={handleRowsPerPageChange}
                  sx={{ minWidth: 74, height: 38, borderRadius: 1.5 }}
                >
                  {[10, 25, 50].map((value) => (
                    <MenuItem key={value} value={value}>
                      {value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Stack>
        )}
      </Paper>
    </Box>
  );
};

export default RequestedProducts;
